/**
 * User Service — hits AMC backend.
 *
 * Entra ID provisions users; AMC stores profile extras and per-department
 * role maps. There is no "add user" here — users appear on first /api/users/me
 * call after Entra sign-in.
 */

import { apiFetch } from './api';

/**
 * @typedef {Object} User
 * @property {string} id                       — Entra oid (uuid)
 * @property {string} email
 * @property {string|null} firstName
 * @property {string|null} lastName
 * @property {string|null} avatarUrl
 * @property {Record<string,'Manager'|'Viewer'>} departmentRoles
 * @property {string} initials                 — derived, 1-2 uppercase letters
 */

function initialsFrom(first, last, email) {
  const f = (first?.[0] || '').toUpperCase();
  const l = (last?.[0] || '').toUpperCase();
  if (f || l) return `${f}${l}`.slice(0, 2);
  return (email?.[0] || '?').toUpperCase();
}

export async function getUsers() {
  const rows = await apiFetch('/api/users');
  return rows.map((u) => ({
    id: u.id,
    email: u.email,
    firstName: u.firstName,
    lastName: u.lastName,
    avatarUrl: u.avatarUrl,
    departmentRoles: u.departmentRoles || {},
    initials: initialsFrom(u.firstName, u.lastName, u.email),
  }));
}

/** Admin upsert: set `oid`'s role in `deptId` (`'Manager'` or `'Viewer'`). */
export async function setUserDepartmentRole(oid, deptId, role) {
  await apiFetch(`/api/users/${oid}/department-roles/${deptId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function removeUserDepartmentRole(oid, deptId) {
  await apiFetch(`/api/users/${oid}/department-roles/${deptId}`, { method: 'DELETE' });
}
